import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Box,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  List,
  ListItemText,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";

import { useNavigate } from "react-router-dom";
import { apiClient } from "@/services/api";

import CreatePerformanceTargetDialog from '../projects/CreatePerformanceTargetDialog.tsx';
import { showSuccessMessage, showErrorMessage } from '@/components/common/GlobalSnackbar.tsx';

// TypeScript 类型定义
interface User {
  id: number;
  username: string;
  role: string;
  org_id: number;
}

interface Project {
  id: number;
  project_name: string;
  project_code: string;
  description?: string;
  user_id: number;
  org_id: number;
  report_template_id?: number;
  industry_type?: string;
  client_level?: string;
  evaluation_type?: string;
  status: "new" | "archived_mentor";
  contract_year?: number;
  dify_dataset_id?: string;
  project_type: string;
  created_at: string;
  updated_at: string;
  report_generation_status?: string;
  report_generation_progress?: number;
  report_generation_error?: string;
  last_generated_at?: string;
  username?: string;
  display_name?: string;  // 创建者显示名称
  organization_name?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`performance-tabpanel-${index}`}
      aria-labelledby={`performance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function PerformanceTargetListItem({
  project,
  onDelete,
  onEdit,
  onNavigate,
  loadingStates,
  getUsernameById,
  formatToGMT8,
}: {
  project: Project;
  onDelete: (project: Project) => void;
  onEdit: (project: Project) => void;
  onNavigate: (projectId: number) => void;
  loadingStates: any;
  getUsernameById: (userId: number) => string;
  formatToGMT8: (dateStr: string | null) => string;
}) {
  // 检查项目是否正在自动处理中
  const isProcessing = project.report_generation_status === "generating";
  // 检查项目是否已完成报告生成
  const isCompleted = project.report_generation_status === "completed";

  return (
    <React.Fragment>
      <div
        onClick={() => {
          console.log('[PerformanceTargetForm] Clicking project:', project.id, 'project_type:', project.project_type, 'project_name:', project.project_name);
          onNavigate(project.id);
        }}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          padding: "12px 0",
          cursor: "pointer",
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <span>{`${project.project_code}-${project.project_name}`}</span>
              {isProcessing && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: "warning.main",
                    color: "warning.contrastText",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                    boxShadow: 1
                  }}
                >
                  <CircularProgress size={14} sx={{ color: "warning.contrastText" }} />
                  <span>自动生成指标中</span>
                  <Box sx={{
                    backgroundColor: "rgba(255,255,255,0.3)",
                    px: 0.5,
                    borderRadius: 0.5,
                    fontSize: "0.75rem"
                  }}>
                    {project.report_generation_progress || 0}%
                  </Box>
                </Box>
              )}
              {isCompleted && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: "success.main",
                    color: "success.contrastText",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                    boxShadow: 1
                  }}
                >
                  <span>报告生成已完成</span>
                  {project.last_generated_at && (
                    <Box sx={{
                      backgroundColor: "rgba(255,255,255,0.3)",
                      px: 0.5,
                      borderRadius: 0.5,
                      fontSize: "0.75rem"
                    }}>
                      {formatToGMT8(project.last_generated_at)}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          }
          secondary={
            <React.Fragment>
              <Box
                sx={{
                  display: "block",
                  mt: 0.5,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {project.description || "暂无描述"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  创建时间: {formatToGMT8(project.created_at)} | 
                  创建者: {project.display_name || project.username || getUsernameById(project.user_id)}
                </Typography>
              </Box>
            </React.Fragment>
          }
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="编辑">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(project);
              }}
              disabled={loadingStates.editing && loadingStates.projectId === project.id}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="删除">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(project);
              }}
              color="error"
              disabled={loadingStates.deleting && loadingStates.projectId === project.id}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </div>
    </React.Fragment>
  );
}

interface PerformanceTargetFormProps {
  // 可以添加props用于接收外部数据或回调
}

const PerformanceTargetForm: React.FC<PerformanceTargetFormProps> = () => {
  const navigate = useNavigate();
  
  // 状态管理
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  // 页签状态
  const [tabValue, setTabValue] = useState(0);
  
  // 过滤状态
  const [filterText, setFilterText] = useState("");
  
  // 对话框状态
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  
  // 加载状态
  const [loadingAction, setLoadingAction] = useState({
    editing: false,
    deleting: false,
    projectId: null as number | null,
  });
  
  // 获取当前用户
  useEffect(() => {
    const userStr = localStorage.getItem("currentUser");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
      } catch (e) {
        console.error("Error parsing current user:", e);
      }
    }
  }, []);

  // 获取用户名
  const getUsernameById = useCallback((userId: number): string => {
    const project = projects.find(p => p.user_id === userId);
    return project?.username || `用户${userId}`;
  }, [projects]);

  // 格式化日期
  const formatToGMT8 = useCallback((dateStr: string | null): string => {
    if (!dateStr) return "未知";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString("zh-CN", {
        timeZone: "Asia/Shanghai",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  }, []);

  // 获取项目列表
  const fetchProjects = useCallback(async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get('/projects/performance-targets', {
        page: 1,
        page_size: 100,
        project_type: "performance_target",
      });
      
      console.log("API Response:", response);
      
      if (response.success && response.data) {
        setProjects(response.data);
        setError(null);
        console.log("Successfully loaded projects:", response.data.length);
      } else {
        console.log("API returned success=false:", response.message);
        setError(response.message || "获取项目列表失败");
      }
    } catch (err: any) {
      console.error("Error fetching projects:", err);
      setError(err.response?.data?.detail || "获取项目列表失败");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // 初始加载
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // 处理项目点击
  const handleNavigate = useCallback((projectId: number) => {
    console.log('[PerformanceTargetForm handleNavigate] projectId:', projectId);
    // 绩效目标项目跳转到指标管理页面
    navigate(`/projects/${projectId}/performance-indicators`);
  }, [navigate]);

  // 处理编辑项目（打开编辑对话框而不是跳转）
  const handleEditProject = useCallback((project: Project) => {
    setProjectToEdit(project);
    setEditDialogOpen(true);
  }, []);

  // 处理删除项目
  const handleDeleteProject = useCallback((project: Project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  }, []);

  // 确认删除项目
  const confirmDeleteProject = useCallback(async () => {
    if (!projectToDelete) return;
    
    setLoadingAction({ editing: false, deleting: true, projectId: projectToDelete.id });
    
    try {
      await apiClient.delete(`/projects/${projectToDelete.id}`);
      showSuccessMessage("项目删除成功");
      fetchProjects(); // 重新获取项目列表
    } catch (err: any) {
      console.error("Error deleting project:", err);
      showErrorMessage(err.response?.data?.detail || "删除项目失败");
    } finally {
      setLoadingAction({ editing: false, deleting: false, projectId: null });
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  }, [projectToDelete, fetchProjects]);

  // 处理创建项目
  const handleCreateProject = useCallback(() => {
    setCreateDialogOpen(true);
  }, []);

  // 处理项目创建/编辑成功
  const handleProjectCreated = useCallback(() => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setProjectToEdit(null);
    fetchProjects(); // 重新获取项目列表
  }, [fetchProjects]);

  // 计算统计数据
  const stats = useMemo(() => {
    const totalInProgress = projects.filter(p => p.status === "new").length;
    const totalArchived = projects.filter(p => p.status === "archived_mentor").length;
    const totalGenerating = projects.filter(p => p.report_generation_status === "generating").length;
    
    return {
      totalInProgress,
      totalArchived,
      totalGenerating,
      total: projects.length
    };
  }, [projects]);

  // 过滤项目
  const filteredProjects = useMemo(() => {
    let filtered = projects;
    
    // 按文本过滤
    if (filterText) {
      filtered = filtered.filter(project =>
        project.project_name.toLowerCase().includes(filterText.toLowerCase()) ||
        project.project_code.toLowerCase().includes(filterText.toLowerCase()) ||
        (project.description && project.description.toLowerCase().includes(filterText.toLowerCase()))
      );
    }
    
    return filtered;
  }, [projects, filterText]);

  // 按状态分组项目
  const inProgressProjects = useMemo(() => 
    filteredProjects.filter(p => p.status === "new"), 
    [filteredProjects]
  );
  
  const archivedProjects = useMemo(() => 
    filteredProjects.filter(p => p.status === "archived_mentor"), 
    [filteredProjects]
  );
  
  const generatingProjects = useMemo(() => 
    filteredProjects.filter(p => p.report_generation_status === "generating"), 
    [filteredProjects]
  );

  if (!currentUser) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const commonListProps = {
    onDelete: handleDeleteProject,
    onEdit: handleEditProject,
    onNavigate: handleNavigate,
    loadingStates: loadingAction,
    getUsernameById,
    formatToGMT8,
  };

  return (
    <Paper elevation={0} sx={{ p: 2, mt: 1, background: 'transparent' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} aria-label="performance target tabs">
          <Tab label={`正在编写 (${stats.totalInProgress})`} id="performance-tab-0" />
          {currentUser?.role === 'super_admin' && (
            <Tab label={`归档报告 (${stats.totalArchived})`} id="performance-tab-1" />
          )}
          {currentUser?.role === 'super_admin' && stats.totalGenerating > 0 && (
            <Tab label={`正在生成报告 (${stats.totalGenerating})`} id="performance-tab-2" />
          )}
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="搜索正在编写的绩效目标..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleCreateProject}
          >
            创建新项目
          </Button>
        </Box>
        <List>
          {inProgressProjects.length > 0 ? (
            inProgressProjects.map((p) => (
              <PerformanceTargetListItem key={p.id} project={p} {...commonListProps} />
            ))
          ) : (
            <Alert severity="info">您当前没有正在编写的绩效目标申报表。</Alert>
          )}
        </List>
      </TabPanel>

      {currentUser?.role === "super_admin" && (
        <TabPanel value={tabValue} index={1}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="搜索归档报告..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
          <List>
            {archivedProjects.length > 0 ? (
              archivedProjects.map((p) => (
                <PerformanceTargetListItem key={p.id} project={p} {...commonListProps} />
              ))
            ) : (
              <Alert severity="info">没有找到符合条件的归档报告。</Alert>
            )}
          </List>
        </TabPanel>
      )}

      {/* 正在生成报告的项目 Tab */}
      {stats.totalGenerating > 0 && (
        <TabPanel value={tabValue} index={2}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Alert severity="info" sx={{ mb: 0 }}>
                正在生成报告的绩效目标申报表会显示在此处，系统会自动更新生成进度。
              </Alert>
            </Box>
          </Box>
          <List>
            {generatingProjects.length > 0 ? (
              generatingProjects.map((p) => (
                <PerformanceTargetListItem key={p.id} project={p} {...commonListProps} />
              ))
            ) : (
              <Alert severity="info">当前没有正在生成报告的绩效目标申报表。</Alert>
            )}
          </List>
        </TabPanel>
      )}

      {/* 创建项目对话框 */}
      <CreatePerformanceTargetDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onProjectCreated={handleProjectCreated}
      />

      {/* 编辑项目对话框 */}
      <CreatePerformanceTargetDialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setProjectToEdit(null); }}
        onProjectCreated={handleProjectCreated}
        editMode
        projectDataToEdit={projectToEdit}
      />

      {/* 删除确认对话框 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除绩效目标申报表 "{projectToDelete?.project_name}" 吗？此操作不可撤销。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button onClick={confirmDeleteProject} color="error" variant="contained">
            删除
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PerformanceTargetForm;